import { Outlet, createFileRoute } from '@tanstack/react-router';

/**
 * /analyze layout. The `t*` search param is the LinkedCursor invariant
 * (ADR 0003) — every chart panel in the workspace reads the same value.
 * Sensors / baseline / pins ride alongside it so a session can be
 * deep-linked. Validating once here lets every child route read the
 * same search shape.
 *
 * The bare /analyze path renders S5 Signal Analysis via analyze.index;
 * /analyze/sessions renders the saved-sessions list via the matching
 * child route. Each child fully owns its layout.
 */
type AnalyzeSearch = {
  /** Selected asset for the workbench. Drives the picker + every panel. */
  asset?: string;
  t?: number;
  sensors?: string[];
  baseline?: string;
  pins?: number[];
  /** Compare-asset overlay ids. Spec allows up to 3. */
  compare?: string[];
};

export const Route = createFileRoute('/$orgId/analyze')({
  validateSearch: (raw: Record<string, unknown>): AnalyzeSearch => {
    const num = (v: unknown) =>
      typeof v === 'number' && Number.isFinite(v) ? v : undefined;
    const strArr = (v: unknown): string[] | undefined => {
      if (!Array.isArray(v)) return undefined;
      const arr = v.filter((x): x is string => typeof x === 'string');
      return arr.length ? arr : undefined;
    };
    const numArr = (v: unknown): number[] | undefined => {
      if (!Array.isArray(v)) return undefined;
      const arr = v.filter((x): x is number => typeof x === 'number');
      return arr.length ? arr : undefined;
    };
    return {
      asset: typeof raw.asset === 'string' ? raw.asset : undefined,
      t: num(raw.t),
      sensors: strArr(raw.sensors),
      baseline:
        typeof raw.baseline === 'string' ? raw.baseline : undefined,
      pins: numArr(raw.pins),
      compare: strArr(raw.compare),
    };
  },
  component: () => <Outlet />,
});
